import { CloseButton, Flex, Text } from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';

import { formatDate, parseDate } from '../../utils/dateUtils';
import { getColumnLabel } from '../../utils/tableUtils';
import { DateFilterValue } from '../filters/DateFilter';
import { isDateFilter, parseFilterStringToObject } from '../filters/filterUtils';
import { NumberFilterMode, NumberFilterValue } from '../filters/NumberFilter';

export const TableFilterStatus = <T,>({
    table,
    hiddenFilterIds = [],
}: {
    table: Table<T>;
    hiddenFilterIds?: string[];
}) => {
    const columnFilters = table.getState().columnFilters;
    const visibleColumnFilters = columnFilters.filter(({ id }) => !hiddenFilterIds.includes(id));

    if (visibleColumnFilters.length === 0) {
        return null;
    }

    return (
        <Flex mb={2} gap={2}>
            {visibleColumnFilters.map((filter) => {
                return (
                    <Flex
                        key={filter.id}
                        flexShrink={0}
                        alignItems="center"
                        borderRadius="md"
                        borderWidth={1}
                        borderColor="gray.600"
                        overflow="hidden"
                    >
                        <Text as="span" px={2} fontSize="sm">
                            {getColumnLabel(filter.id)}
                        </Text>
                        <Flex background="gray.600" align="center">
                            <Text as="span" px={2} fontSize="sm">
                                {getFormattedFilterValue(filter.value)}
                            </Text>
                            <CloseButton
                                borderRadius={0}
                                size="sm"
                                onClick={() =>
                                    table.setColumnFilters(columnFilters.filter(({ id }) => id !== filter.id))
                                }
                            />
                        </Flex>
                    </Flex>
                );
            })}
        </Flex>
    );
};

const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const getFormattedFilterValue = (value: unknown) => {
    if (Array.isArray(value)) {
        return value.join(', ');
    }

    const parsed = parseFilterStringToObject(value as string);
    if (parsed) {
        if (isDateFilter(parsed)) {
            const { from, to } = parsed as DateFilterValue;

            if (from && to) {
                return `${formatDate(parseDate(from))} - ${formatDate(parseDate(to))}`;
            } else if (from) {
                return `from ${formatDate(parseDate(from))}`;
            } else if (to) {
                return `to ${formatDate(parseDate(to))}`;
            }
        } else {
            const { eq, gt, gte, lt, lte, mode } = parsed as NumberFilterValue;
            if (eq) {
                return `= ${formatter.format(Number(eq))}`;
            }
            if (mode === NumberFilterMode.OR) {
                return [
                    (lt || lte) && 'value',
                    lt && `< ${formatter.format(Number(lt))}`,
                    lte && `≤ ${formatter.format(Number(lte))}`,
                    (lt || lte) && (gt || gte) && 'or',
                    gt && `${formatter.format(Number(gt))} <`,
                    gte && `${formatter.format(Number(gte))} ≤`,
                    (gt || gte) && 'value',
                ]
                    .filter(Boolean)
                    .join(' ');
            } else {
                return [
                    gt && `${formatter.format(Number(gt))} <`,
                    gte && `${formatter.format(Number(gte))} ≤`,
                    'value',
                    lt && `< ${formatter.format(Number(lt))}`,
                    lte && `≤ ${formatter.format(Number(lte))}`,
                ]
                    .filter(Boolean)
                    .join(' ');
            }
        }
    }

    return String(value);
};
